import { CQAppConstant } from 'app-constants';
import { IUpgradeSchema } from 'interfaces/IUpgradeSchema.interface';
import { Statuses } from 'interfaces/ITask.interface';
import { ITaskRun } from '../interfaces/IHistory.interface';
import { SFConnection } from './sf-connector';
import { parseVersion } from 'sf-app/sf-package';
import { CQMgmtServiceProvider } from 'services/cq-mgmt.service';
import i18n from 'i18n';

/**
 * Checks if the module identifier is for updates.
 *
 * @param {string | null} moduleIdentifier - The module identifier to check.
 * @returns {boolean} True if the module identifier is for updates, false otherwise.
 */
export function isUpdatesModule(moduleIdentifier?: string | null): boolean {
    return moduleIdentifier === CQAppConstant.MODULE_TYPES.UPDATES;
}

/**
 * Checks if the module identifier is for upgrades.
 *
 * @param {string | null} moduleIdentifier - The module identifier to check.
 * @returns {boolean} True if the module identifier is for upgrades, false otherwise.
 */
export function isUpgradesModule(moduleIdentifier?: string | null): boolean {
    return moduleIdentifier === CQAppConstant.MODULE_TYPES.UPGRADES;
}

/**
 * Checks if the module identifier is for refresh.
 *
 * @param {string | null} moduleIdentifier - The module identifier to check.
 * @returns {boolean} True if the module identifier is for refresh, false otherwise.
 */
export function isRefreshModule(moduleIdentifier?: string | null): boolean {
    return moduleIdentifier === CQAppConstant.MODULE_TYPES.REFRESH;
}

/**
 * Checks if the module identifier is for fresh installation.
 *
 * @param {string | null} moduleIdentifier - The module identifier to check.
 * @returns {boolean} True if the module identifier is for fresh installation, false otherwise.
 */
export function isFreshModule(moduleIdentifier?: string | null): boolean {
    return moduleIdentifier === CQAppConstant.MODULE_TYPES.FRESH;
}

/**
 * Gets the module tab name based on the module identifier.
 *
 * @param {string | null} moduleIdentifier - The module identifier.
 * @returns {string} The module tab name.
 */
export function getModuleTabName(moduleIdentifier?: string | null): string {
    if (isUpdatesModule(moduleIdentifier)) {
        return CQAppConstant.MODULE_TABS.UPDATES;
    }
    if (isRefreshModule(moduleIdentifier)) {
        return CQAppConstant.MODULE_TABS.REFRESH;
    }
    return CQAppConstant.MODULE_TABS.UPGRADE_PLUS;
}

/**
 * Gets the label key for the action button based on the module identifier.
 *
 * @param {string | null} moduleIdentifier - The module identifier.
 * @returns {string} The label key for the action button.
 */
export function getModuleActionButtonLabelKey(moduleIdentifier?: string | null): string {
    if (isUpdatesModule(moduleIdentifier)) {
        return 'task_start_update';
    }
    if (isRefreshModule(moduleIdentifier)) {
        return 'task_start_refresh';
    }
    if (isFreshModule(moduleIdentifier)) {
        return 'task_start_install';
    }
    return 'task_start_upgrade';
}

/**
 * Gets the process type based on the module identifier.
 *
 * @param {string | null} moduleIdentifier - The module identifier.
 * @returns {string} The process type.
 */
export function getProcessType(moduleIdentifier?: string | null): string {
    if (isUpdatesModule(moduleIdentifier)) {
        return CQAppConstant.MODULE_TYPES.UPDATE;
    }
    if (isRefreshModule(moduleIdentifier)) {
        return CQAppConstant.MODULE_TYPES.REFRESH;
    }
    if (isFreshModule(moduleIdentifier)) {
        return CQAppConstant.MODULE_TYPES.FRESH;
    }
    if (isUpgradesModule(moduleIdentifier)) {
        return CQAppConstant.MODULE_TYPES.UPGRADE;
    }
    return moduleIdentifier || CQAppConstant.MODULE_TYPES.UPGRADE;
}

/**
 * Gets the lowercase process type based on the module identifier.
 *
 * @param {string | null} moduleIdentifier - The module identifier.
 * @returns {string} The lowercase process type.
 */
export function getProcessTypeLowercase(moduleIdentifier?: string | null): string {
    return getProcessType(moduleIdentifier).toLowerCase();
}

/**
 * Gets the i18n label key and process type for progress UI based on the current module.
 *
 * @param {string | null} currentModule - The current module identifier.
 * @returns {{ labelKey: string; process: string }} i18n label key and process type.
 */
export function getProcessAndLabelKey(currentModule?: string | null): { labelKey: string; process: string } {
    if (isRefreshModule(currentModule)) {
        return { labelKey: 'Refresh_progress', process: CQAppConstant.MODULE_TABS.REFRESH };
    }
    if (isUpdatesModule(currentModule)) {
        return { labelKey: 'Updates_progress', process: CQAppConstant.MODULE_TABS.UPDATES };
    }
    if (isFreshModule(currentModule)) {
        return { labelKey: 'Fresh_progress', process: CQAppConstant.MODULE_TABS.FRESH };
    }
    return { labelKey: 'Updates_progress', process: CQAppConstant.MODULE_TABS.UPGRADES };
}

/**
 * Gets the installed packages history key based on the module identifier.
 *
 * @param {string | null} moduleIdentifier - The module identifier.
 * @returns {string | undefined} The installed packages history key.
 */
export function getInstalledPackagesHistoryKey(moduleIdentifier?: string | null): string | undefined {
    switch (moduleIdentifier) {
        case CQAppConstant.MODULE_TYPES.UPDATES:
            return CQAppConstant.CQ.UPDATES;
        case CQAppConstant.MODULE_TYPES.UPGRADES:
            return CQAppConstant.CQ.UPGRADES;
        case CQAppConstant.MODULE_TYPES.REFRESH:
            return CQAppConstant.CQ.REFRESH;
        case CQAppConstant.MODULE_TYPES.FRESH:
            return CQAppConstant.CQ.FRESH;
    }
}

/**
 * Gets the module identifier for the given tab index.
 *
 * Note: Fresh installation is never rendered as a tab (it's only ever surfaced via
 * the welcome mat - see CQMainScreen/CQModule), so there is no tab index for it here.
 *
 * @param {number} tabIndex - The tab index.
 * @returns {string | undefined} The module identifier for the tab index.
 */
export function getModuleIdentifierForTabIndex(tabIndex: number): string | undefined {
    switch (tabIndex) {
        case 0:
            return CQAppConstant.MODULE_TYPES.UPDATES;
        case 1:
            return CQAppConstant.MODULE_TYPES.UPGRADES;
        case 2:
            return CQAppConstant.MODULE_TYPES.REFRESH;
        default:
            return undefined;
    }
}

/**
 * Gets the note type based on the module identifier.
 *
 * @param {string | null} moduleIdentifier - The module identifier.
 * @returns {string} The note type.
 */
export function getNoteType(moduleIdentifier?: string | null): string {
    if (isUpdatesModule(moduleIdentifier)) {
        return `${CQAppConstant.MODULE_TYPES.UPDATE.toLowerCase()} release`;
    }
    return `${moduleIdentifier?.toLowerCase()} release`;
}

/**
 * Gets the upgraded type display name based on the module identifier.
 *
 * @param {string | null} moduleIdentifier - The module identifier.
 * @returns {string} The upgraded type display name.
 */
export function getUpgradedType(moduleIdentifier?: string | null): string {
    return CQAppConstant.UPGRADED_TYPES[moduleIdentifier?.toUpperCase() as keyof typeof CQAppConstant.UPGRADED_TYPES];
}

/**
 * Gets the dynamic migration display name from the module's versionName.
 * Extracts the version (e.g., "CQ Summer '26") from versionName and appends "Migration Execution".
 *
 * @param {IUpgradeSchema} module - The module containing the versionName.
 * @returns {string} The migration display name (e.g., "CQ Summer '26 Migration Execution").
 */
export function getMigrationDisplayName(module: IUpgradeSchema): string {
    const version = module.versionName?.split(CQAppConstant.SHARED_MIGRATION.VERSION_NAME_PREFIX)?.[1] || " ";
    return `${version} ${CQAppConstant.SHARED_MIGRATION.DISPLAY_NAME}`;
}

/**
 * Gets the heading text based on the module type.
 *
 * @param {string | null} currentModule - The current module identifier.
 * @param {IUpgradeSchema} module - The module containing installation tasks.
 * @returns {string} The heading text for the module.
 */
export function getHeadingText(currentModule: string | null, module: IUpgradeSchema): string {
    if (module.name === CQAppConstant.SHARED_MIGRATION.SCRIPT_NAME) {
        return getMigrationDisplayName(module);
    }

    if (isUpdatesModule(currentModule)) {
        return `Install ${module.forModule} Version ${module.moduleIdentifier.split(' ')[2]}`;
    }
    if (isUpgradesModule(currentModule)) {
        return `Install ${module.forModule}`;
    }
    if (isRefreshModule(currentModule)) {
        return module.forModule;
    }
    return `Install ${module.forModule}`;
}


/**
 * Returns the display label for a given module type.
 *
 * @param forModule - Module type identifier
 * @returns Corresponding module label
 */
export function getModuleLabel(forModule: string) {
    switch (forModule) {
        case CQAppConstant.MODULE_TYPES.REFRESH:
            return CQAppConstant.POST_REFRESH;
        default:
            return forModule;
    }
}

/**
 * Gets the icon name and color variant for a task status indicator.
 *
 * @param isSkipped - Whether the task is skipped
 * @param status - The task status
 * @param wasPreviouslyCompleted - Whether the skipped task was already completed in a prior run
 * @returns Object with icon name and color variant
 */
export function getIconVariantFromTaskStatus(isSkipped: boolean, status: Statuses, wasPreviouslyCompleted = false) {
    if (isSkipped) {
        if (wasPreviouslyCompleted) {
            return { icon: CQAppConstant.TASK_ICON_NAMES.CHECK, colorVariant: CQAppConstant.TASK_COLOR_VARIANTS.SUCCESS };
        }
        return { icon: CQAppConstant.TASK_ICON_NAMES.CLOSE, colorVariant: CQAppConstant.TASK_COLOR_VARIANTS.LIGHT };
    }
    if (status === Statuses.Failed) {
        return { icon: CQAppConstant.TASK_ICON_NAMES.CLOSE, colorVariant: CQAppConstant.TASK_COLOR_VARIANTS.ERROR };
    }
    if (status === Statuses.Completed) {
        return { icon: CQAppConstant.TASK_ICON_NAMES.CHECK, colorVariant: CQAppConstant.TASK_COLOR_VARIANTS.SUCCESS };
    }
    return { icon: CQAppConstant.TASK_ICON_NAMES.RECORD, colorVariant: CQAppConstant.TASK_COLOR_VARIANTS.LIGHT };
}

/**
 * Compares two version strings using `SFPackageVersion`'s existing major/minor/patch
 * comparison (see sf-app/sf-package.tsx) instead of re-implementing it here.
 *
 * @returns A negative number if `a` < `b`, a positive number if `a` > `b`, 0 if equal.
 */
export function compareVersions(a: string, b: string): number {
    const va = parseVersion(a);
    const vb = parseVersion(b);
    if (va.isSameAs(vb)) return 0;
    return va.isNewerThan(vb) ? 1 : -1;
}

const LEGACY_RELEASE_NOTES_URL = CQAppConstant.RELEASE_NOTES.find(entry => entry.minVersion === '0.0.0')!.url;

/**
 * Picks the release notes URL for the highest `CQAppConstant.RELEASE_NOTES` entry that
 * `finalVersion` meets or exceeds. Falls back to the legacy (0.0.0) entry's URL if
 * `finalVersion` is missing, since it can't be compared against the array at all.
 *
 * @param finalVersion - The target package version, e.g. "16.6.2".
 * @returns The release notes URL to link to.
 */
export function getReleaseNotesUrl(finalVersion?: string | null): string {
    if (!finalVersion) {
        return LEGACY_RELEASE_NOTES_URL;
    }
    // Keep only release notes entries whose minVersion is at or below finalVersion (i.e. finalVersion qualifies for them).
    const matchingEntries = CQAppConstant.RELEASE_NOTES.filter(
        entry => compareVersions(finalVersion, entry.minVersion) >= 0
    );
    // Among those, pick the one with the highest minVersion — the closest release boundary.
    const newest = [...matchingEntries].sort(
        (a, b) => compareVersions(b.minVersion, a.minVersion)
    )[0];
    return newest?.url ?? LEGACY_RELEASE_NOTES_URL;
}

/**
 * Returns the highest `finalVersion` among a list of schemas, preferring ComplianceQuest Core
 * and falling back to extensions only if no Core schema is present.
 *
 * @param items - Schemas (upgrades or patches) each carrying a `packageName` and `finalVersion`.
 * @returns The highest version string found, or `undefined` if none have one.
 */
export function getLatestFinalVersion(items: { packageName?: string; finalVersion?: string }[]): string | undefined {
    const versionsOf = (schemas: typeof items) => schemas
        .map(item => item.finalVersion)
        .filter((version): version is string => !!version)
        .sort(compareVersions);

    const coreVersions = versionsOf(items.filter(item => item.packageName === CQAppConstant.COMPLIANCEQUEST));
    return (coreVersions.length ? coreVersions : versionsOf(items)).pop();
}

/**
 * Resolves the Core version for Updates release notes from the org's currently installed
 * Core version. Never falls back to an extension's version.
 *
 * @returns The Core version to resolve release notes from, or `undefined` if Core isn't installed.
 */
export function getCoreVersionForReleaseNotes(): string | undefined {
    const installedCore = CQMgmtServiceProvider.getCachedInstalledPackages().find(pkg => pkg.name === CQAppConstant.COMPLIANCEQUEST);
    return installedCore?.version.toString();
}

/**
 * Formats the time taken to complete a task, given its start and end timestamps.
 *
 * @param startedOn - When the task started
 * @param endedOn - When the task ended
 * @returns Formatted duration (e.g. "2min", "1hr 5min", "1 Day 5hr"), or "" if the task hasn't completed
 */
export function formatTaskDuration(startedOn?: Date, endedOn?: Date): string {
    if (!startedOn || !endedOn) {
        return '';
    }
    const diffMs = new Date(endedOn).getTime() - new Date(startedOn).getTime();
    return formatDurationMs(diffMs);
}

/**
 * Formats a duration given in milliseconds into a human-readable string.
 *
 * @param diffMs - Duration in milliseconds
 * @returns Formatted duration (e.g. "2min 5sec", "1hr 5min 3sec", "1 Day 5hr 2min 1sec"), or "" if diffMs is not positive
 */
export function formatDurationMs(diffMs: number): string {
    if (!diffMs || diffMs <= 0) {
        return '';
    }

    const totalSeconds = Math.max(1, Math.floor(diffMs / CQAppConstant.TIME_CONSTANTS.MS_IN_SECOND));
    const days = Math.floor(totalSeconds / CQAppConstant.TIME_CONSTANTS.SECONDS_IN_DAY);
    const hours = Math.floor((totalSeconds % CQAppConstant.TIME_CONSTANTS.SECONDS_IN_DAY) / CQAppConstant.TIME_CONSTANTS.SECONDS_IN_HOUR);
    const minutes = Math.floor((totalSeconds % CQAppConstant.TIME_CONSTANTS.SECONDS_IN_HOUR) / CQAppConstant.TIME_CONSTANTS.SECONDS_IN_MINUTE);
    const seconds = totalSeconds % CQAppConstant.TIME_CONSTANTS.SECONDS_IN_MINUTE;

    const parts: string[] = [];
    if (days > 0) {
        parts.push(`${days} ${i18n.t(days > 1 ? 'duration_days' : 'duration_day')}`);
    }
    if (hours > 0) {
        parts.push(`${hours}${i18n.t('duration_hr')}`);
    }
    if (minutes > 0) {
        parts.push(`${minutes}${i18n.t('duration_min')}`);
    }
    if (seconds > 0) {
        parts.push(`${seconds}${i18n.t('duration_sec')}`);
    }
    return parts.join(' ');
}

/**
 * Sums the elapsed time of each completed task run and formats it as a single duration.
 *
 * Merged tasks (e.g. batched deploy packages) share the same startedOn/endedOn across
 * all their subtask runs, so each distinct time window is only counted once.
 *
 * @param tasks - The task runs to sum durations for
 * @returns Formatted total duration (e.g. "2min", "1hr 5min"), or "" if no task has completed
 */
export function formatTotalTaskDuration(tasks: ITaskRun[]): string {
    const seenWindows = new Set<string>();
    const totalMs = tasks.reduce((sum, tsk) => {
        if (!tsk.startedOn || !tsk.endedOn) {
            return sum;
        }
        const startedOnMs = new Date(tsk.startedOn).getTime();
        const endedOnMs = new Date(tsk.endedOn).getTime();
        const windowKey = `${startedOnMs}-${endedOnMs}`;
        if (seenWindows.has(windowKey)) {
            return sum;
        }
        seenWindows.add(windowKey);

        const diffMs = endedOnMs - startedOnMs;
        return diffMs > 0 ? sum + diffMs : sum;
    }, 0);
    return formatDurationMs(totalMs);
}

/**
 * Determines the navigation label and URL based on task type and migration report availability.
 * Falls back to deployment status if no migration report URL is provided.
 * @param taskType - The type of task being executed
 * @param migrationReportUrl - The URL for the migration report (empty string if not available)
 * @returns Navigation label (i18n key) and URL to open
 */
export function getNavigationLabelAndUrl(
    taskType: string | undefined,
    migrationReportUrl: string
): { label: string; url: string } {
    const baseUrl = SFConnection.getBaseURL();
    const deploymentStatusUrl = baseUrl + CQAppConstant.DEPLOYMENT_STATUS_PATH;
    const migrationDashboardUrl = baseUrl + CQAppConstant.MIGRATION_REPORT_DASHBOARD_PATH;
    const hasMigrationReport = migrationReportUrl && migrationReportUrl !== deploymentStatusUrl;

    if (taskType === CQAppConstant.TASK_TYPES.MIGRATION_TASK_TYPE_V2) {
        return {
            label: 'click_to_navigate_migration_dashboard',
            url: migrationDashboardUrl
        };
    }

    if (taskType === CQAppConstant.TASK_TYPES.MIGRATION_TASK_TYPE && hasMigrationReport) {
        return {
            label: 'click_to_navigate_migration_logs_report',
            url: migrationReportUrl
        };
    }
    
    return {
        label: 'click_to_view_deployment_status',
        url: deploymentStatusUrl
    };
}